import React, { useEffect } from "react"
import { CalculationSchema } from "src/features/calculator/schema"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"
import styled from "styled-components"
import {
  calcTotalReimbursement,
  calcSelfPayment,
  calcCustomerTransferFee,
  calcRemittance,
} from "src/features/calculator/utils/formulas/basicFormula"
import { calcCustomerTransferFeeRate } from "src/features/calculator/utils/formulas/reMaxRevoFormula"
import { TotalReimbursementRow } from "src/features/calculator/components/formula/row/TotalReimbursementRow"
import { SelfPaymentRow } from "src/features/calculator/components/formula/row/SelfPaymentRow"
import { CustomerTransferFeeRow } from "src/features/calculator/components/formula/row/CustomerTransferFeeRow"
import { useCalculatedResult } from "src/features/calculator/hooks/useCalculatedResult"
import { RemittanceRow } from "src/features/calculator/components/formula/row/RemittanceRow"
import { FixedCustomerTransferFeeRateRow } from "src/features/calculator/components/formula/row/FixedCustomerTransferFeeRateRow"
import { useFormValueUpdate } from "src/features/calculator/hooks/useFormValueUpdate"

type Props = {
  setValue: UseFormSetValue<CalculationSchema>
  watch: UseFormWatch<CalculationSchema>
  errors: FieldErrors<CalculationSchema>
}

export const ReMaxRevoFormula = ({ setValue, watch, errors }: Props) => {
  const watchedValues = watch()
  const {
    customerTransferFeeRate,
    totalReimbursement,
    customerTransferFee,
    maxAdvancePayment,
    initialCost,
    downPayment,
    rent,
  } = watchedValues

  const calculatedResult = useCalculatedResult({
    values: watchedValues,
    setValue,
    calculation: {
      customerTransferFeeRate: calcCustomerTransferFeeRate(),
      totalReimbursement: calcTotalReimbursement({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      selfPayment: calcSelfPayment({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      customerTransferFee: calcCustomerTransferFee({
        rent,
        customerTransferFeeRate,
      }),
      remittance: calcRemittance({
        totalReimbursement,
        customerTransferFee,
      }),
    },
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFeeRate,
    currentValue: customerTransferFeeRate,
    fieldName: "customerTransferFeeRate",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.totalReimbursement,
    currentValue: totalReimbursement,
    fieldName: "totalReimbursement",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFee,
    currentValue: customerTransferFee,
    fieldName: "customerTransferFee",
    setValue,
  })

  return (
    <StyledCalculatorDiv>
      <FixedCustomerTransferFeeRateRow
        customerTransferFeeRate={calculatedResult.customerTransferFeeRate}
      />
      <TotalReimbursementRow
        totalReimbursement={calculatedResult.totalReimbursement}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <SelfPaymentRow
        selfPayment={calculatedResult.selfPayment}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <CustomerTransferFeeRow
        customerTransferFee={calculatedResult.customerTransferFee}
        rent={rent}
        customerTransferFeeRate={customerTransferFeeRate}
        setValue={setValue}
        errors={errors}
      />
      <RemittanceRow
        remittance={calculatedResult.remittance}
        totalReimbursement={totalReimbursement}
        customerTransferFee={customerTransferFee}
        setValue={setValue}
        errors={errors}
      />
    </StyledCalculatorDiv>
  )
}

const StyledCalculatorDiv = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`
